import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, ORDER_SEPARATOR, shuffle, useGame, useRoundStyle } from '../sdk/react';

const FEEDBACK_HOLD_MS = 700;

type Sequence = { label: string; items: string[] };

function gridClass(n: number): string {
  if (n <= 4) return 'cols-2';
  if (n <= 9) return 'cols-3';
  return 'cols-4';
}

export default function SequenceTap() {
  const game = useGame();
  const { kit } = game;
  const sequences = (Array.isArray(game.config.sequences) ? game.config.sequences : [])
    .filter((s): s is Sequence => !!s && typeof (s as Sequence).label === 'string' && Array.isArray((s as Sequence).items) && (s as Sequence).items.length >= 2);
  const [seq, setSeq] = useState<Sequence | null>(null);
  const [trialId, setTrialId] = useState<string | null>(null);
  const [order, setOrder] = useState<number[]>([]);
  const [tapped, setTapped] = useState<number[]>([]);
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [fading, setFading] = useState(false);
  const playRef = useRef<() => void>(() => {});
  const { style, typeClass } = useRoundStyle();

  const start = useCallback(async () => {
    if (sequences.length === 0) return;
    setDone(false);
    setTapped([]);
    setStep(0);
    kit.speech.stop();
    const chosen = sequences[Math.floor(Math.random() * sequences.length)];
    const positions = shuffle(chosen.items.map((_, i) => i));
    const id = await game.register({ label: chosen.label, tiles: positions.map((i) => chosen.items[i]) }, { value: chosen.items.join(ORDER_SEPARATOR) });
    if (!id) return;
    setSeq(chosen);
    setOrder(positions);
    setTrialId(id);
  }, [kit, game.config]);

  useEffect(() => { void start(); }, [start]);

  playRef.current = () => { if (seq && !game.textPrompt) game.say(seq.label); };

  useEffect(() => {
    if (!seq) return;
    const t = window.setTimeout(() => playRef.current(), 350);
    return () => window.clearTimeout(t);
  }, [seq, trialId]);

  useEffect(() => {
    if (!done) return;
    const fade = window.setTimeout(() => setFading(true), FEEDBACK_HOLD_MS);
    const advance = window.setTimeout(() => { void start().finally(() => setFading(false)); }, FEEDBACK_HOLD_MS + game.transitionMs);
    return () => { window.clearTimeout(fade); window.clearTimeout(advance); };
  }, [done]);

  async function tap(position: number) {
    if (!seq || !trialId || done || tapped.includes(position)) return;
    if (order[position] !== step) return;
    const nextStep = step + 1;
    setTapped((t) => [...t, position]);
    setStep(nextStep);
    if (nextStep < seq.items.length) return;
    const verdict = await game.respond(trialId, seq.items.join(ORDER_SEPARATOR));
    if (!verdict) return;
    setDone(true);
  }

  return (
    <GameFrame game={game} empty={sequences.length === 0} emptyText="Edit it to add an order to learn." onResume={() => { void start(); }} breakTrial={done ? null : trialId}>
      {seq && (
        <>
          <div className="concept-prompt-text" aria-live="polite">{seq.label}</div>
          <div className="concept-sequence-row" aria-label="Order so far">
            {seq.items.map((it, i) => (
              <span key={i} className={`concept-seq-slot${i < step ? ' filled' : ''}`}>{i < step ? it : i + 1}</span>
            ))}
          </div>
          <div className={`tile-grid ${gridClass(order.length)} ${typeClass}${fading ? ' fading-out' : ''}`} style={style} role="group" aria-label="Sequence tiles">
            {order.map((itemIndex, position) => {
              const used = tapped.includes(position);
              return (
                <button
                  key={`${position}-${itemIndex}`}
                  className={`tile${used ? ' correct' : ''}`}
                  onClick={() => void tap(position)}
                  disabled={used || done}
                  aria-label={seq.items[itemIndex]}
                >
                  {seq.items[itemIndex]}
                </button>
              );
            })}
          </div>
        </>
      )}
    </GameFrame>
  );
}
